import React, { useEffect, useState } from 'react';
import { observer } from 'mobx-react';
import { Button, Collapse, Form, Row } from 'antd';
import { SaveOutlined } from '@ant-design/icons';
import { EmailRecipientsEdit } from '../EmailRecipients/EmailRecipientsEdit';
import cx from 'classnames';
import { ValidateErrorEntity } from 'rc-field-form/lib/interface';
import {
    IAntDFormChangeFieldName,
    IFormFieldChange,
    IFormFields,
    IGetApiType,
    NotificationType,
    ToggleItem,
    companyConfigCardIMEITypesFields,
    companyConfigCardSetTotalTypesFields,
    companyConfigCardSetTypesFields,
    companyConfigCardTypesFields,
    companyConfigCompanyTypesFields,
} from '../../interfaces';
import { useCompanyConfig, useProfile, useProfileFeatureFlags, useUiIsSpinnerFound } from '../../hooks';
import {
    addNotification,
    apiMessagesDeleteMessageByFieldName,
    apiMessagesGetFormRules,
    apiMessagesGetFormRulesFieldBlank,
    cleanCompanyStore,
    cleanMyAlarmRelationSelectors,
    getCardSetsSelect,
    getCompanyConfig,
    getMyAlarmRelationSelectors,
    rulesIsNumber,
    rulesMoreThanZero,
    saveProfileAlarms,
} from '../../middleware';
import { ITrField, tB, tF, tT } from '../../translate';
import { CardInfoToolBar, CardMainLayout, Catcher, cardLayoutOptions } from '../../Components';
import {
    MonthlyCardPanel,
    MonthlyCardSetPanel,
    PanelControl,
    PanelTogglesControlMyAlarm,
    ToggleList,
    ToggleListMyAlarm,
} from './Components';
import { AdvancedFieldTypes } from './Components/AdvancedField';
import Styles from '../styles/m_itemCardStyles.less';

export const MyAlarms: React.FC = observer(() => {
    const { Panel } = Collapse;
    const [form] = Form.useForm();
    const formName = 'profileAlarms';
    const componentSpinners: IGetApiType[] = ['GET_USER_PROFILE', 'GET_COMPANY_CONFIG', 'PATCH_COMPANY_CONFIG'];
    const [isSending, setSending] = useState(false);
    const isLoading = useUiIsSpinnerFound(componentSpinners);

    const { companyId = '' } = useProfile();
    const initialValues = useCompanyConfig();
    const isDisabled = isLoading || isSending;
    const { newAlarms: isNewAlarmsFlag } = useProfileFeatureFlags();

    const companyBalanceAlarms: ToggleItem[] = [
        {
            label: '1st balance warning',
            name: 'notification_company_threshold_medium_reached',
        },
        {
            label: '2nd balance warning',
            name: 'notification_company_threshold_high_reached',
        },
        {
            label: 'No Balance',
            name: 'notification_company_balance_limit_reached',
        },
    ];
    const checkValid = async (): Promise<void> => {
        await form.validateFields();
    };

    const IMEILockAlarms: ToggleItem[] = [
        {
            label: 'Send an alarm when a different IMEI is recorded for IMEI locked cards',
            name: 'notification_card_imei_lock_violation',
        },
    ];

    useEffect(() => {
        if (companyId) {
            getCompanyConfig(companyId);
            getMyAlarmRelationSelectors(companyId);
        }
        return (): void => {
            cleanCompanyStore();
            cleanMyAlarmRelationSelectors();
        };
    }, [companyId]);

    useEffect(() => {
        form.setFieldsValue(initialValues);
    }, [JSON.stringify(initialValues)]);

    useEffect(() => {
        getCardSetsSelect({ companyId }, true);
    }, [companyId]);

    const findAnError = (errorInfo: ValidateErrorEntity): void => {
        errorInfo.errorFields.forEach((value) => {
            const errorFieldName = value.name;
            const elementWithError = document.getElementById(`${formName}_${errorFieldName}`);
            const collapse = elementWithError?.closest('.ant-collapse-item');
            if (!collapse?.classList.contains('ant-collapse-item-active')) {
                const button = collapse?.getElementsByClassName('ant-collapse-header')[0] as HTMLElement;
                button && button.click();
            }
        });
        setTimeout(() => {
            const firstErrorFieldName = errorInfo.errorFields[0].name;
            const elementWithError = document.getElementById(`${formName}_${firstErrorFieldName}`);
            elementWithError?.scrollIntoView({ block: 'center', behavior: 'smooth' });
        }, 200);
    };

    const isFormValid = (): boolean => form.getFieldsError().some((item) => item.errors.length > 0);

    const onFinish = async (data: IFormFields): Promise<void> => {
        if (!isFormValid()) {
            setSending(true);
            const isError = await saveProfileAlarms({ id: initialValues.id, ...data });
            if (!isError) {
                addNotification({
                    type: NotificationType.INFO,
                    message: 'Alarm config has been saved!',
                    duration: 5,
                });

                await getCompanyConfig(companyId);
                form.resetFields();
            } else {
                checkValid();
            }

            setSending(false);
        } else {
            const errorFields = form.getFieldsError().filter((item) => item.errors.length > 0);
            const errorInfo: ValidateErrorEntity = {
                outOfDate: false,
                errorFields: errorFields,
                values: form.getFieldsValue(),
            };
            findAnError(errorInfo);
        }
    };

    const resetFields = (arr: string[]): void => {
        form.resetFields([...arr]);
    };

    const changeError = (name: string, errorMessage: string, isError: boolean): void => {
        if (isError) {
            form.setFields([
                {
                    name,
                    errors: [`${errorMessage}`],
                },
            ]);
        } else {
            form.setFields([
                {
                    name,
                    errors: [],
                },
            ]);
        }
    };

    const onFieldsChange = (changedFields: IFormFieldChange[]): void => {
        const fieldNameChange = changedFields && changedFields[0] && changedFields[0].name;
        if (fieldNameChange) {
            apiMessagesDeleteMessageByFieldName(fieldNameChange as IAntDFormChangeFieldName);
        }
    };

    const companyFields = companyConfigCompanyTypesFields.map((i) => i as ITrField);
    const cardIMEIFields = companyConfigCardIMEITypesFields.map((i) => i as ITrField);
    const cardFields = companyConfigCardTypesFields.map((i) => i as ITrField);
    const cardSetFields = companyConfigCardSetTypesFields.map((i) => i as ITrField);
    const cardSetTotalFields = companyConfigCardSetTotalTypesFields.map((i) => i as ITrField);

    return (
        <Catcher>
            <article className={Styles.main}>
                <Row>
                    <CardMainLayout
                        columnOption={cardLayoutOptions.cardInfoEdit}
                        title={'Alarms'}
                        idSpinners={componentSpinners}
                    >
                        <p>
                            {tT('You can define the alarms you want to receive below.')} <br />
                            {tT('Please enter the requested information and configure the different options below.')}
                        </p>
                        <Form
                            form={form}
                            name="profileAlarms"
                            initialValues={initialValues}
                            onFinish={onFinish}
                            onFinishFailed={findAnError}
                            onFieldsChange={onFieldsChange}
                            scrollToFirstError
                            layout={'horizontal'}
                            className={'formStyle'}
                        >
                            <Collapse>
                                <Panel key={1} header={tF('Company Balance')} forceRender={true}>
                                    {isNewAlarmsFlag ? (
                                        <ToggleListMyAlarm fields={companyFields} form={form} isLoading={isDisabled} />
                                    ) : (
                                        <ToggleList toggleArr={companyBalanceAlarms} isLoading={isDisabled} />
                                    )}
                                </Panel>
                                <Panel key={2} header={tF('IMEI Lock')} forceRender={true}>
                                    {isNewAlarmsFlag ? (
                                        <ToggleListMyAlarm fields={cardIMEIFields} form={form} isLoading={isDisabled} />
                                    ) : (
                                        <ToggleList toggleArr={IMEILockAlarms} isLoading={isDisabled} />
                                    )}
                                </Panel>

                                <Panel
                                    key={3}
                                    header={tF('Monthly Card Balance limits for all cards')}
                                    forceRender={true}
                                >
                                    {isNewAlarmsFlag ? (
                                        <PanelTogglesControlMyAlarm
                                            advancedField={[
                                                {
                                                    name: 'cardLimit',
                                                    rules: [
                                                        ...apiMessagesGetFormRulesFieldBlank(),
                                                        rulesIsNumber,
                                                        rulesMoreThanZero,
                                                    ],
                                                    placeholder: 'Limit',
                                                    type: AdvancedFieldTypes.INPUT,
                                                },
                                            ]}
                                            form={form}
                                            isLoading={isLoading}
                                            isSending={isSending}
                                            mainToggleLabel={'Set monthly limit for all company cards limit'}
                                            togglesFields={cardFields}
                                        />
                                    ) : (
                                        <PanelControl
                                            label={tF('Set monthly limit for all company cards limit')}
                                            isSending={isSending}
                                            isLoading={isLoading}
                                            Content={MonthlyCardPanel}
                                            togglesNames={[
                                                'notification_card_threshold_50_reached',
                                                'notification_card_threshold_75_reached',
                                                'notification_card_threshold_100_reached',
                                                'suspend_card_threshold_100_reached',
                                            ]}
                                            resetFields={resetFields}
                                            changeError={changeError}
                                        />
                                    )}
                                </Panel>
                                <Panel
                                    key={4}
                                    header={tF('Card monthly Balance limits for all cards in card set')}
                                    forceRender={true}
                                >
                                    {isNewAlarmsFlag ? (
                                        <PanelTogglesControlMyAlarm
                                            advancedField={[
                                                {
                                                    name: 'cardSetLimit',
                                                    rules: [
                                                        ...apiMessagesGetFormRulesFieldBlank(),
                                                        rulesIsNumber,
                                                        rulesMoreThanZero,
                                                    ],
                                                    placeholder: 'Limit',
                                                    type: AdvancedFieldTypes.INPUT,
                                                },
                                                {
                                                    name: 'cardSetIds',
                                                    label: 'Choose card sets',
                                                    rules: apiMessagesGetFormRules,
                                                    type: AdvancedFieldTypes.SELECT_CARD_SETS,
                                                },
                                            ]}
                                            form={form}
                                            isLoading={isLoading}
                                            isSending={isSending}
                                            mainToggleLabel={'Set monthly limit for card set(s)'}
                                            togglesFields={cardSetFields}
                                        />
                                    ) : (
                                        <PanelControl
                                            label={tF('Set monthly limit for card set(s)')}
                                            isSending={isSending}
                                            isLoading={isLoading}
                                            Content={MonthlyCardSetPanel}
                                            togglesNames={[
                                                'notification_card_set_threshold_50_reached',
                                                'notification_card_set_threshold_75_reached',
                                                'notification_card_set_threshold_100_reached',
                                                'suspend_card_set_threshold_100_reached',
                                            ]}
                                            resetFields={resetFields}
                                            changeError={changeError}
                                        />
                                    )}
                                </Panel>
                                {isNewAlarmsFlag ? (
                                    <Panel key={5} header={tF('Monthly Card Set Balance limits')} forceRender={true}>
                                        <PanelTogglesControlMyAlarm
                                            advancedField={[
                                                {
                                                    name: 'cardSetTotalLimit',
                                                    rules: [
                                                        ...apiMessagesGetFormRulesFieldBlank(),
                                                        rulesIsNumber,
                                                        rulesMoreThanZero,
                                                    ],
                                                    placeholder: 'Limit',
                                                    type: AdvancedFieldTypes.INPUT,
                                                },
                                                {
                                                    name: 'cardSetTotalIds',
                                                    label: 'Choose card sets',
                                                    rules: apiMessagesGetFormRules,
                                                    type: AdvancedFieldTypes.SELECT_CARD_SETS,
                                                },
                                            ]}
                                            form={form}
                                            isLoading={isLoading}
                                            isSending={isSending}
                                            mainToggleLabel={'Monthly Card Set Balance limits'}
                                            togglesFields={cardSetTotalFields}
                                        />
                                    </Panel>
                                ) : null}
                                <Panel key={6} header={tF('Alarm recipients')} forceRender={true}>
                                    <EmailRecipientsEdit name={'alarmEmails'} form={form} />
                                </Panel>
                            </Collapse>
                            <CardInfoToolBar>
                                <Button
                                    type="primary"
                                    htmlType="submit"
                                    icon={<SaveOutlined />}
                                    className={cx(Styles.mainButton, {
                                        [Styles.buttonsDisable]: isSending,
                                    })}
                                    disabled={isDisabled}
                                    loading={isDisabled}
                                >
                                    {tB('Update')}
                                </Button>
                            </CardInfoToolBar>
                        </Form>
                    </CardMainLayout>
                </Row>
            </article>
        </Catcher>
    );
});
